/**
 * API Configuration Utility for Portal Customer
 *
 * Provides URL configurations for API and WebSocket endpoints.
 * Adapted for Next.js with SSR guards.
 */

const isBrowser = typeof window !== "undefined";

export interface ApiConfig {
  domain: string;
  url: string;
  ssl: boolean;
}

export interface WsConfig {
  url: string;
  origin: string;
}

/**
 * Get API configuration based on current hostname
 */
export const getApiConfig = (): ApiConfig => {
  const config: ApiConfig = {
    domain: "",
    url: "",
    ssl: false,
  };

  // Use environment variable as primary source
  const envUrl = process.env.NEXT_PUBLIC_API_URL;
  if (envUrl) {
    config.url = envUrl;
    config.ssl = envUrl.startsWith("https");
    try {
      config.domain = new URL(envUrl).hostname;
    } catch {
      config.domain = "localhost";
    }
    return config;
  }

  // SSR fallback
  if (!isBrowser) {
    config.url = "http://localhost:5003";
    config.domain = "localhost";
    config.ssl = false;
    return config;
  }

  const hostname = window.location.hostname;

  // Portal Customer production URLs
  if (hostname === "customer.spacecoastsrcmgt.com") {
    config.url = "https://api-customer.spacecoastsrcmgt.com";
    config.ssl = true;
    config.domain = "api-customer.spacecoastsrcmgt.com";
  } else if (hostname === "portal.staffcontrol.cloud") {
    config.url = "https://api-customer.spacecoastsrcmgt.com";
    config.ssl = true;
    config.domain = "api-customer.spacecoastsrcmgt.com";
  } else if (hostname.includes("azureedge.net") || hostname.includes("azurestaticapps.net")) {
    // Azure development/staging
    config.url = "https://api-customer.spacecoastsrcmgt.com";
    config.ssl = true;
    config.domain = "api-customer.spacecoastsrcmgt.com";
  } else {
    // Local development
    config.url = "http://localhost:5003";
    config.domain = "localhost";
    config.ssl = false;
  }

  return config;
};

/**
 * Get WebSocket/SignalR configuration based on current hostname
 */
export const getWsConfig = (): WsConfig => {
  const config: WsConfig = {
    url: "",
    origin: "",
  };

  // SSR fallback
  if (!isBrowser) {
    config.url = "http://localhost:5003/hubs/datasync";
    config.origin = "http://localhost:3001";
    return config;
  }

  const hostname = window.location.hostname;

  // Portal Customer production URLs - SignalR Hub
  if (hostname === "customer.spacecoastsrcmgt.com") {
    config.url = "https://api-customer.spacecoastsrcmgt.com/hubs/datasync";
    config.origin = "https://customer.spacecoastsrcmgt.com";
  } else if (hostname === "portal.staffcontrol.cloud") {
    config.url = "https://api-customer.spacecoastsrcmgt.com/hubs/datasync";
    config.origin = "https://portal.staffcontrol.cloud";
  } else if (hostname.includes("azureedge.net") || hostname.includes("azurestaticapps.net")) {
    // Azure development/staging
    config.url = "https://api-customer.spacecoastsrcmgt.com/hubs/datasync";
    config.origin = `https://${hostname}`;
  } else {
    // Local development - SignalR Hub
    config.url = "http://localhost:5003/hubs/datasync";
    config.origin = "http://localhost:3001";
  }

  return config;
};

/**
 * Get activity hub URL (for presence, typing indicators, etc.)
 */
export const getActivityHubConfig = (): WsConfig => {
  const wsConfig = getWsConfig();
  return {
    url: wsConfig.url.replace("/hubs/datasync", "/hubs/activity"),
    origin: wsConfig.origin,
  };
};
